<div class="mb-4 text-2xl font-bold">Purchase Orders</div>

@if ((loading$ | async) === true) {
  <aux-table-skeleton />
}

@if ((loading$ | async) === false) {
  @if (filteredGridData()) {
    <aux-table-panel class="adjustment-panel">
      <div class="flex justify-between p-[15px]">
        <form [formGroup]="POForm" class="filters flex w-full items-center space-x-4">
          <div>
            <span class="mb-1 block text-xs">Vendor Contract</span>
            <ng-select
              class="big-dropdown tabular-nums"
              style="width: 150px"
              placeholder="Select"
              formControlName="selected_vendors"
              bindValue="id"
              bindLabel="name"
              [multiple]="true"
              [searchable]="true"
              [items]="filteredVendorOptions()"
              [closeOnSelect]="false"
              data-pendo-id="po_vendor_contract_dropdown"
            >
              <ng-template let-item="item" ng-option-tmp>
                <div
                  class="block truncate"
                  #text
                  [auxTooltip]="text.offsetWidth < text.scrollWidth ? item.name : ''"
                  [auxTooltipPositions]="optionTooltipPositions"
                >
                  {{ item.name }}
                </div>
              </ng-template>
              <ng-template let-items="items" let-clear="clear" ng-multi-label-tmp>
                @if (items.length === 1) {
                  <div class="ng-value">
                    @for (item of castItemsToVendorOptions(items) | slice: 0 : 1; track item.name) {
                      <div class="flex">
                        <span class="ng-value-icon left" aria-hidden="true" (click)="clear(item)">
                          ×
                        </span>
                        <span
                          class="ng-value-label max-w-[70px] overflow-hidden text-ellipsis"
                          #text
                          [auxTooltip]="text.offsetWidth < text.scrollWidth ? item.name : ''"
                          [auxTooltipPositions]="optionTooltipPositions"
                        >
                          {{ item.name }}
                        </span>
                      </div>
                    }
                  </div>
                } @else if (items.length > 1) {
                  <div class="ng-value">
                    <span class="ng-value-label">{{ items.length }} Selected</span>
                  </div>
                }
              </ng-template>
            </ng-select>
          </div>
          <aux-multi-select-dropdown
            placeholder="Select"
            label="PO #"
            class="w-[150px]"
            bindLabel="po_number"
            formControlName="purchase_order"
            bindValue="id"
            [searchable]="true"
            [multiple]="true"
            [items]="filteredPONumbers()"
            [closeOnSelect]="false"
            data-pendo-id="po_number_dropdown"
          />

          <aux-multi-select-dropdown
            class="block w-[150px]"
            placeholder="All"
            label="Invoices Through"
            [items]="dateOptions()"
            [formControlName]="'invoices_through'"
            [bindLabel]="'label'"
            [bindValue]="'value'"
            [multiple]="false"
            [attr.data-pendo-id]="'po_invoices_through_dropdown'"
          />

          <aux-checkbox
            class="mt-4"
            [checked]="POForm.get('pos_with_variance')?.value || false"
            (customChange)="varianceCheckbox($event)"
            data-pendo-id="po_show_variances_checkbox"
          >
            <div class="text-sm">Show Variances</div>
          </aux-checkbox>
        </form>

        <div class="flex w-full items-start justify-end space-x-2">
          <aux-button-group
            label="Add"
            variant="primary"
            [position]="buttonGroupOverlayPosition"
            [options]="buttonGroupOptions()"
            [disabled]="purchaseOrderCtx.disabledAddPoUI()"
            [template]="
              purchaseOrderCtx.addPoActionTooltips().length > 1 ? errorArrayTooltip : undefined
            "
            [auxTooltip]="
              purchaseOrderCtx.addPoActionTooltips().length === 1
                ? purchaseOrderCtx.addPoActionTooltip()
                : ''
            "
          />

          <ng-template #errorArrayTooltip>
            <aux-errors-tooltip [errors]="tooltipErrors" />
          </ng-template>

          <aux-button
            label="Upload PO Report"
            pendoTag="upload-po-report"
            (click)="onAddPoReportUploadClick()"
            [disabled]="!hasUploadReportPermission() || isChangeOrdersWorkflowLocked()"
            [template]="
              !hasUploadReportPermission() && isChangeOrdersWorkflowLocked()
                ? poReportErrorArrayTooltip
                : undefined
            "
            [auxTooltip]="
              !hasUploadReportPermission() && isChangeOrdersWorkflowLocked()
                ? ''
                : uploadPOReportTooltipErrors[0]
            "
          />

          <ng-template #poReportErrorArrayTooltip>
            <aux-errors-tooltip [errors]="uploadPOReportTooltipErrors" />
          </ng-template>

          <aux-export-excel-button
            [clickFN]="onExport.bind(this)"
            [spinnerSize]="7"
            pendoTag="purchase-order-excel-export-button"
          />
        </div>
      </div>

      <div class="p-[15px] pt-0">Last PO Report Updated: {{ lastPOReportUpdatedText() }}</div>

      <ag-grid-angular
        style="max-width: 100%"
        class="ag-theme-aux flat-table-theme tabular-nums"
        [rowData]="filteredGridData()"
        [gridOptions]="(gridOptions$ | async)!"
        domLayout="autoHeight"
        [components]="components"
        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onGridReady($event)"
        auxStickyGrid
      />
    </aux-table-panel>
  }

  <div class="mt-8 flex justify-between">
    <div>
      <div class="text-xl font-bold">Invoiced to Date, by PO</div>

      <div class="mt-4"></div>
      <div class="mb-4 flex items-center space-x-4 text-sm">
        <div class="flex items-center space-x-2">
          <div class="h-4 w-4 rounded-full border bg-aux-blue"></div>
          <div>Invoices Received</div>
        </div>
        <div class="flex items-center space-x-2">
          <div class="h-4 w-4 rounded-full border bg-aux-green-secondary"></div>
          <div>Invoices Paid</div>
        </div>
      </div>
    </div>
  </div>
  <div class="grid grid-cols-12 gap-5 text-sm">
    <div class="col-span-10"></div>
    <div class="col-span-2 font-bold">Total PO ({{ getSelectedCurrencySymbol() }})</div>

    @for (po of filteredGridData(); track po) {
      <div class="col-span-10 font-medium">PO #{{ po.po_number }}</div>
      <div class="col-span-2"></div>

      <div class="col-span-10 flex text-xs font-bold">
        @if (po.received_percentage > 0) {
          <div
            class="flex h-5 items-center justify-end rounded-l pr-8"
            [ngClass]="{
              'bg-aux-blue': po.received_percentage <= 100,
              'bg-aux-error': po.received_percentage > 100,
              'rounded-r': po.paid_percentage === 0 && po.received_percentage > 100,
            }"
            [ngStyle]="{ flexGrow: po.received_percentage }"
          >
            <span class="relative left-[2px] tabular-nums text-white">
              {{ po.received_amount | money: po.currency }} ({{ po.received_percentage }}%)
            </span>
          </div>
        }
        @if (po.paid_percentage > 0) {
          <div
            class="flex h-5 items-center justify-end bg-aux-green-secondary pr-8"
            [ngStyle]="{ flexGrow: po.paid_percentage }"
            [ngClass]="{
              'rounded-l': po.received_percentage === 0,
              'rounded-r': po.paid_percentage + po.received_percentage > 100,
            }"
          >
            <span class="relative left-[2px] tabular-nums text-white">
              {{ po.paid_amount | money: po.currency }} ({{ po.paid_percentage }}%)
            </span>
          </div>
        }
        @if (100 - (po.received_percentage + po.paid_percentage); as remaining_percentage) {
          @if (remaining_percentage > 0) {
            <div
              class="flex h-5 items-center rounded-r bg-aux-gray-light pl-8"
              [ngClass]="{
                'rounded-l': remaining_percentage === 100,
              }"
              [ngStyle]="{ flexGrow: remaining_percentage }"
            >
              @if (remaining_percentage === 100) {
                <span>—</span>
              }
            </div>
          }
        }
      </div>
      <div
        class="col-span-2 flex items-center text-xs tabular-nums text-aux-gray-darkest opacity-75"
      >
        {{ po.linkedActivityTotal | money: po.currency }}
      </div>
    }
  </div>
}
